import React, { useMemo, useState } from "react";

function getOverrideCandidateKeys(node) {
  const definitionDefaults = node?.definition?.defaultParameters || {};
  const bindings = node?.parameterBindings || {};
  const ownKeys = node?.ownParameterKeys || [];

  return Array.from(
    new Set([
      ...Object.keys(definitionDefaults),
      ...Object.keys(bindings),
      ...ownKeys,
    ])
  ).filter(
    (key) =>
      key &&
      !key.startsWith("parent_") &&
      !key.startsWith("project_") &&
      !key.startsWith("global_")
  );
}

function toRows(obj = {}) {
  return Object.entries(obj).map(([key, value], index) => ({
    id: `${key}_${index}`,
    key,
    value: value ?? "",
  }));
}

function parseValue(value) {
  const raw = String(value ?? "").trim();
  if (raw === "") return "";

  const num = Number(raw);
  return Number.isFinite(num) ? num : value;
}

function toObject(rows = []) {
  const result = {};

  rows.forEach((row) => {
    const key = String(row.key || "").trim();
    if (!key) return;
    result[key] = parseValue(row.value);
  });

  return result;
}

function createEmptyRow() {
  return {
    id: `ovr_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    key: "",
    value: "",
  };
}

function ValueInput({ value, onChange }) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="900 or custom value"
      className="w-full px-3 py-2 border rounded text-sm"
    />
  );
}

export default function InstanceParameterOverridesInspector({
  node,
  resolvedNode,
  onChange,
}) {

  const sourceNode = resolvedNode || node;

  const [showAddModal, setShowAddModal] = useState(false);

  const rows = useMemo(
  () => toRows(sourceNode?.parameterOverrides || {}),
  [sourceNode?.parameterOverrides]
);

  const candidateKeys = useMemo(
  () => getOverrideCandidateKeys(sourceNode),
[sourceNode]
);

const existingKeys = useMemo(
  () => new Set(rows.map((r) => r.key).filter(Boolean)),
  [rows]
);

const availableKeys = useMemo(
  () => candidateKeys.filter((key) => !existingKeys.has(key)),
  [candidateKeys, existingKeys]
);

  if (!sourceNode) {
    return (
      <div className="border rounded p-4 bg-gray-50 text-sm text-gray-500">
        Select a child node to override parameter values.
      </div>
    );
  }

  function applyRows(nextRows) {
  const nextOverrides = toObject(nextRows);

 // generated node prvo
if (resolvedNode?.parentGeneratedFromId) {
  // postojeća shelf / partition / overrideKey logika ostaje ovde
}

// real node tek posle
if (node?.id) {
  onChange?.({
    ...node,
    parameterOverrides: nextOverrides,
  });
  return;
}

  const parentId = resolvedNode.parentGeneratedFromId;

  // shelf
  if (resolvedNode.partitionSide === "shelf" && resolvedNode.panelKey) {
    const currentShelfOverrides = resolvedNode.parentShelfOverrides || {};
    const current = currentShelfOverrides[resolvedNode.panelKey] || {};

    onChange?.({
      id: parentId,
      shelfOverrides: {
        ...currentShelfOverrides,
        [resolvedNode.panelKey]: {
          ...current,
          definitionId: current.definitionId || resolvedNode.definitionId,
          parameterOverrides: nextOverrides,
        },
      },
    });
    return;
  }

  // partition panel
  if (resolvedNode.panelKey) {
    const side = resolvedNode.partitionSide || "front";
    const parentPartitions = resolvedNode.parentPartitions || {};
    const current =
      parentPartitions?.[side]?.panelOverrides?.[resolvedNode.panelKey] || {};

    onChange?.({
      id: parentId,
      partitions: {
        ...parentPartitions,
        [side]: {
          ...(parentPartitions[side] || {}),
          panelOverrides: {
            ...(parentPartitions[side]?.panelOverrides || {}),
            [resolvedNode.panelKey]: {
              ...current,
              definitionId: current.definitionId || resolvedNode.definitionId,
              parameterOverrides: nextOverrides,
            },
          },
        },
      },
    });
    return;
  }

  // generated side panel
  if (resolvedNode.overrideKey) {
    const currentOverrides = resolvedNode.parentChildDefinitionOverrides || {};
    const current = currentOverrides[resolvedNode.overrideKey] || {};

    const nextChildOverrides = {
      ...currentOverrides,
      [resolvedNode.overrideKey]: {
        ...current,
        definitionId: current.definitionId || resolvedNode.definitionId,
        parameterOverrides: nextOverrides,
      },
    };

    onChange?.({
      id: parentId,
      childDefinitionOverrides: nextChildOverrides,
      parentChildDefinitionOverrides: nextChildOverrides,
    });
  }
}

  function handleAdd() {
  if (!availableKeys.length) {
    alert("Nema dostupnih parametara za override ili su svi već dodati.");
    return;
  }

  setShowAddModal(true);
}

function addSelectedParam(key) {
  if (!key) return;

  applyRows([
    ...(rows || []),
    {
      id: createEmptyRow().id,
      key,
      value: "",
    },
  ]);

  setShowAddModal(false);
}

  function handleRemove(rowId) {
    applyRows(rows.filter((row) => row.id !== rowId));
  }

  function handleChange(rowId, field, value) {
    applyRows(
      rows.map((row) =>
        row.id === rowId ? { ...row, [field]: value } : row
      )
    );
  }

  function applyQuickSize(width, height, depth) {
    applyRows(
      toRows({
        ...toObject(rows),
        width,
        height,
        depth,
      })
    );
  }

  function clearOverrides() {
    const confirmed = window.confirm("Clear all parameter overrides?");
    if (!confirmed) return;
    applyRows([]);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold">Parameter Overrides</h3>
          <div className="text-xs text-gray-500">
            Override values only for this selected instance.
          </div>
        </div>

        <button
          type="button"
          onClick={handleAdd}
          className="px-3 py-2 rounded bg-blue-600 text-white text-sm"
        >
          + Add
        </button>
      </div>

      <datalist id="override-keys">
  {availableKeys.map((key) => (
    <option key={key} value={key} />
  ))}
</datalist>

      <div className="border rounded p-3 bg-gray-50 space-y-2">
        <div className="text-sm font-semibold">Quick sizes</div>

        <div className="flex flex-wrap gap-2">
             <button
            type="button"
            onClick={() => applyQuickSize(600, 720, 580)}
            className="px-3 py-1 rounded border bg-white text-xs"
          >
            Cabinet 600×720×580
          </button>
          <button
            type="button"
            onClick={() => applyQuickSize(600, 2000, 580)}
            className="px-3 py-1 rounded border bg-white text-xs"
          >
            Column 600×2000×580
          </button>
          <button
            type="button"
            onClick={() => applyQuickSize(600, 700, 300)}
            className="px-3 py-1 rounded border bg-white text-xs"
          >
            Column 600×700×300
          </button>
          <button
            type="button"
            onClick={() => applyQuickSize(600, 720, 18)}
            className="px-3 py-1 rounded border bg-white text-xs"
          >
            Board 600×720×18
          </button>
          <button
            type="button"
            onClick={() => applyQuickSize(900, 600, 18)}
            className="px-3 py-1 rounded border bg-white text-xs"
          >
            Board 900×600×18
          </button>
          <button
            type="button"
            onClick={() => applyQuickSize(100, 100, 100)}
            className="px-3 py-1 rounded border bg-white text-xs"
          >
            Cube 100
          </button>
        </div>
      </div>

      {!rows.length ? (
        <div className="border rounded p-4 bg-gray-50 text-sm text-gray-500">
          No overrides. This instance uses definition defaults and bindings.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((row) => (
            <div key={row.id} className="border rounded p-3 bg-white space-y-2">
              <div className="grid grid-cols-5 gap-2 items-end">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Parameter
                  </label>
                  <input
                    list="override-keys"
                    value={row.key}
                    onChange={(e) =>
                      handleChange(row.id, "key", e.target.value)
                    }
                    placeholder="width"
                    className="w-full px-3 py-2 border rounded text-sm"
                  />
                </div>

                <div className="col-span-3">
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Value
                  </label>
                  <ValueInput
                    value={row.value}
                    onChange={(value) =>
                      handleChange(row.id, "value", value)
                    }
                  />
                </div>
              </div>

              <div className="flex justify-between items-center">
                <div className="text-[11px] text-gray-400">
                  Numbers are saved as numbers. Text stays as text.
                </div>

                <button
                  type="button"
                  onClick={() => handleRemove(row.id)}
                  className="px-2 py-1 rounded border text-red-600 text-xs"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {rows.length ? (
        <button
          type="button"
          onClick={clearOverrides}
          className="w-full px-3 py-2 rounded border text-red-600 bg-white text-sm"
        >
          Clear overrides
        </button>
      ) : null}

      {showAddModal && (
  <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center">
    <div className="bg-white rounded-lg shadow-xl w-[420px] max-h-[80vh] overflow-auto p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-semibold">Add override parameter</div>
          <div className="text-xs text-gray-500">
            Izaberi parametar koji želiš da promeniš za ovaj element.
          </div>
        </div>

        <button
          type="button"
          onClick={() => setShowAddModal(false)}
          className="text-gray-500"
        >
          ✕
        </button>
      </div>

      <div className="grid grid-cols-1 gap-2">
        {availableKeys.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => addSelectedParam(key)}
            className="text-left px-3 py-2 border rounded hover:bg-blue-50 text-sm"
          >
            {key}
          </button>
        ))}
      </div>
    </div>
  </div>
)}
    </div>
  );
}